import type { LayoutMode, PeerId, Quality } from '@live-stream/shared'

/**
 * The layout engine — a pure function. No DOM, no Vue.
 *
 * Given who is in the room, how big the stage is and what the person (or the
 * host) has asked for, it says where every tile goes. The component that
 * renders it only translates rectangles into transforms. Everything about
 * WHICH layout, and why, lives here where it can be unit-tested against the
 * plan's matrix: 1/2/3/4/9/25 participants × all-off / mixed / share.
 */

export interface ParticipantView {
  peerId: PeerId
  videoOn: boolean
  /**
   * The shape of their picture, width over height, once a frame has said it. A portrait phone
   * then gets a tile of its own shape in the window's grid, as in Meet, instead of a wide one
   * with blur beside it.
   */
  aspect?: number
  speaking: boolean
  isLocal: boolean
  joinedAt: number
}

export interface ScreenShareView {
  peerId: PeerId
  trackId: string
}

export interface LayoutInput {
  participants: ParticipantView[]
  containerW: number
  containerH: number
  screenShare: ScreenShareView | null
  /** Host spotlight. Overrides every local pin, and the UI says so. */
  focusedPeerId: PeerId | null
  /** This client's own pin. Never leaves the client. */
  localPinnedPeerId: PeerId | null
  /**
   * The server's auto spotlight: whoever talked for the dwell uninterrupted
   * while the host had auto spotlight on. Centre stage for everyone, below a
   * host spotlight and a local pin.
   */
  autoSpotlightPeerId?: PeerId | null
  mode: LayoutMode
  isMobile: boolean
  /** Tiles beyond this many are not rendered; the engine reports them as hidden. */
  maxTiles?: number
  gap?: number
  /**
   * The window's stage. Tiles fill the space they are given and crop, as
   * Meet's do, rather than keeping 16:9 with bars around them; and the
   * people under a featured tile keep a card size you can read and scroll
   * sideways, rather than shrinking to fit. The recorder leaves it off: its
   * frame is 16:9 and everything in it is letterboxed.
   */
  fill?: boolean
  /**
   * Your own camera while anyone else is here: a tile like everyone's, or
   * `float` — out of the layout, a small picture of yourself the UI draws
   * over the others and you can move, as calls on a phone have it. Alone,
   * or featured by a host, you are laid out like anyone.
   */
  selfView?: 'tile' | 'float'
  /**
   * The page of the grid to show, from 0. A meeting bigger than a page
   * (`maxTiles`) is paged rather than shrunk; past the last page is the last.
   */
  page?: number
  /**
   * Who gets a seat first: peer ids in order (seats.ts keeps it, bringing
   * whoever talks onto the first page). Anyone not in it follows, in the
   * order they arrived. Without it, the order they arrived.
   */
  order?: PeerId[]
  /**
   * What the UI draws over the stage's edges — a top bar, the controls —
   * on a phone, where the featured tile runs under them edge to edge. A
   * grid of several and the strip of cards keep clear of them.
   */
  insets?: { top: number; bottom: number }
  /** A big meeting: ask for less (qualityForWidth). The recorder leaves it off. */
  large?: boolean
  /**
   * Device pixels per CSS pixel (at most 2 is worth it): a phone's 390-pixel tile is over a
   * thousand pixels across, and the 360p picture asked for from its width alone was stretched
   * soft. The recorder leaves it off (its frame is in pixels already).
   */
  pixelRatio?: number
}

/**
 * What is IN the meeting, apart from the surface it is drawn on. The stage
 * lays this scene out for the window; the recorder lays the same scene out
 * again for the recording's own 16:9 frame, with the same rules.
 */
export type LayoutScene = Omit<
  LayoutInput,
  | 'containerW'
  | 'containerH'
  | 'isMobile'
  | 'maxTiles'
  | 'gap'
  | 'fill'
  | 'selfView'
  | 'page'
  | 'order'
  | 'insets'
  | 'large'
  | 'pixelRatio'
>

export type ResolvedMode =
  'presentation' | 'spotlight' | 'sidebar' | 'equal-split' | 'avatar-grid' | 'grid'

export interface LayoutTile {
  peerId: PeerId
  kind: 'camera' | 'screen'
  x: number
  y: number
  w: number
  h: number
  z: number
  /** What to ask the transport for, from the tile's size. 'off' when nothing would show. */
  quality: Quality
  showVideo: boolean
  /** The stage's main tile: the share, the spotlight, the pin. */
  primary: boolean
}

export interface LayoutResult {
  mode: ResolvedMode
  tiles: LayoutTile[]
  /** Participants with no tile on this page. Ask for no video for them. */
  hidden: PeerId[]
  /** Which rule chose the primary, so the UI can say "the host is spotlighting…". */
  primaryBy: 'share' | 'host' | 'pin' | 'auto' | 'none'
  /**
   * With `fill`, the row of cards under the featured tile: where it sits and
   * how wide it is. It can be wider than the stage; the stage scrolls it.
   */
  strip?: { y: number; h: number; width: number }
  /** With `selfView: 'float'`: ourselves, left out of the tiles for the UI to draw apart. */
  floating?: PeerId
  /** The grid's page on show, from 0, and how many pages there are. */
  page?: number
  pages?: number
}

const ASPECT = 16 / 9
const DEFAULT_GAP = 8
const DEFAULT_MAX_TILES = 16
/** The strip's cards, with `fill`: a little squarer than 16:9, as in the designs. */
const CARD_ASPECT = 16 / 10
/** Avatar cells past this are just empty space around a circle. */
const AVATAR_CELL_MAX = 220
/** At most this many cards in a focus strip; the rest are counted, not drawn. */
const STRIP_MAX = 48
/** A phone's focus strip: small portrait cards floating over the featured tile. */
const PHONE_CARD = { w: 66, h: 88 }

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

const round = (n: number): number => Math.round(n)
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

/**
 * Tile width → the quality worth asking for. A 160px thumbnail should not
 * receive 720p. In a big meeting (`large`) the bar is higher, because each
 * thing asked for is sent to a thousand people: the full picture only for
 * a tile that fills a big screen, the middle size for a phone or a laptop
 * window, the smallest for a card.
 */
export function qualityForWidth(w: number, large = false): Quality {
  if (large) return w < 240 ? 'low' : w < 960 ? 'med' : 'high'
  if (w < 180) return 'low'
  if (w < 640) return 'med'
  return 'high'
}

/**
 * The quality worth asking for a camera tile of w×h CSS pixels: from the pixels it has to fill,
 * not its width alone. A tall tile (a phone's whole screen) shows a landscape picture zoomed in
 * (cameraFrame crops up to half of it), so it needs up to twice its width in the picture. Small
 * cards are not worth the extra.
 */
export function qualityForTile(w: number, h: number, ratio = 1, large = false): Quality {
  // A big meeting asks for as little as it can: the screen's pixels are not counted.
  if (large) return qualityForWidth(w, true)
  const zoom = h > w && w >= 200 ? 2 : 1
  const needed = w * zoom * clamp(ratio, 1, 2)
  return qualityForWidth(needed)
}

const QUALITY_ORDER: readonly Quality[] = ['off', 'low', 'med', 'high']

/**
 * The more demanding of two asks. A peer's video has two consumers while a
 * recording runs — the tile on this screen and the tile in the recording's
 * frame — and is subscribed at whichever wants more.
 */
export function higherQuality(a: Quality, b: Quality): Quality {
  return QUALITY_ORDER.indexOf(a) >= QUALITY_ORDER.indexOf(b) ? a : b
}

/** The largest `aspect` rectangle that fits in a cell. */
function fitIn(cellW: number, cellH: number, aspect: number): { w: number; h: number } {
  const w = Math.max(0, Math.min(cellW, cellH * aspect))
  return { w, h: w / aspect }
}

function fitCentred(area: Rect, aspect: number): Rect {
  const { w, h } = fitIn(area.w, area.h, aspect)
  return {
    x: round(area.x + (area.w - w) / 2),
    y: round(area.y + (area.h - h) / 2),
    w: round(w),
    h: round(h),
  }
}

/**
 * The (cols, rows) that gives n tiles of `aspect` the largest area in W×H.
 * Brute force over cols: n is small, and at 49 it is still free.
 */
export function bestGrid(
  n: number,
  W: number,
  H: number,
  gap: number,
  aspect: number,
): { cols: number; rows: number; tileW: number; tileH: number } {
  let best = { cols: 1, rows: Math.max(1, n), tileW: 0, tileH: 0 }
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    const cellW = (W - gap * (cols - 1)) / cols
    const cellH = (H - gap * (rows - 1)) / rows
    if (cellW <= 0 || cellH <= 0) continue
    const { w, h } = fitIn(cellW, cellH, aspect)
    if (w * h > best.tileW * best.tileH) best = { cols, rows, tileW: w, tileH: h }
  }
  return best
}

/** How narrow and how wide a tile may be: a phone held upright, a wide webcam. */
const MIN_ASPECT = 9 / 16

/** Whether the window's grid should follow the cameras' own shapes: only when one is portrait. */
function followsShapes(people: readonly ParticipantView[]): boolean {
  return people.some((p) => p.videoOn && p.aspect !== undefined && p.aspect < 1)
}

/** The shape a person's tile takes: their picture's when it shows, else the usual 16:9. */
const shapeOf = (p: ParticipantView): number =>
  p.videoOn && p.aspect !== undefined ? clamp(p.aspect, MIN_ASPECT, WIDEST) : ASPECT

/**
 * Tiles of different shapes in rows of one height each, as Meet lays them out: every row as tall
 * as the stage allows, a portrait phone beside a wide webcam at the same height, rows centred.
 * The people stay in order; the number of rows is the one that makes the tiles tallest.
 */
function justifiedRows(aspects: readonly number[], area: Rect, gap: number): Rect[] {
  const n = aspects.length
  if (n === 0) return []
  const total = aspects.reduce((a, b) => a + b, 0)
  let best: { h: number; rows: number[][] } | null = null
  for (let r = 1; r <= n; r++) {
    const rows: number[][] = []
    let row: number[] = []
    let sum = 0
    for (let i = 0; i < n; i++) {
      row.push(i)
      sum += aspects[i] as number
      const left = r - rows.length
      const itemsAfter = n - (i + 1)
      if (left > 1 && (sum >= (total / r) * (rows.length + 1) - 1e-9 || itemsAfter === left - 1)) {
        rows.push(row)
        row = []
      }
    }
    if (row.length > 0) rows.push(row)
    const fromWidth = Math.min(
      ...rows.map((idx) => {
        const rowSum = idx.reduce((a, i) => a + (aspects[i] as number), 0)
        return (area.w - gap * (idx.length - 1)) / rowSum
      }),
    )
    const h = Math.min(fromWidth, (area.h - gap * (rows.length - 1)) / rows.length)
    if (h > 0 && (!best || h > best.h + 0.5)) best = { h, rows }
  }
  if (!best) return aspects.map(() => ({ x: area.x, y: area.y, w: 0, h: 0 }))
  const { h, rows } = best
  const gridH = rows.length * h + (rows.length - 1) * gap
  const out: Rect[] = new Array(n)
  rows.forEach((idx, ri) => {
    const widths = idx.map((i) => h * (aspects[i] as number))
    const rowW = widths.reduce((a, b) => a + b, 0) + gap * (idx.length - 1)
    let x = area.x + (area.w - rowW) / 2
    idx.forEach((i, k) => {
      out[i] = roundRect({
        x,
        y: area.y + (area.h - gridH) / 2 + ri * (h + gap),
        w: widths[k] as number,
        h,
      })
      x += (widths[k] as number) + gap
    })
  })
  return out
}

/** n tiles in a cols×rows grid centred in the area; an incomplete last row is centred too. */
function placeGrid(
  n: number,
  cols: number,
  rows: number,
  tileW: number,
  tileH: number,
  area: Rect,
  gap: number,
): Rect[] {
  const gridH = rows * tileH + (rows - 1) * gap
  const top = area.y + (area.h - gridH) / 2
  const rects: Rect[] = []
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols)
    const inRow = row === rows - 1 ? n - row * cols : cols
    const rowW = inRow * tileW + (inRow - 1) * gap
    const left = area.x + (area.w - rowW) / 2
    const col = i - row * cols
    rects.push({
      x: round(left + col * (tileW + gap)),
      y: round(top + row * (tileH + gap)),
      w: round(tileW),
      h: round(tileH),
    })
  }
  return rects
}

/** k tiles in a horizontal strip, all the same size, centred. */
function placeStrip(k: number, area: Rect, gap: number): Rect[] {
  if (k === 0) return []
  const maxW = (area.w - gap * (k - 1)) / k
  const { w, h } = fitIn(maxW, area.h, ASPECT)
  const total = k * w + (k - 1) * gap
  const left = area.x + (area.w - total) / 2
  const top = area.y + (area.h - h) / 2
  return Array.from({ length: k }, (_, i) => ({
    x: round(left + i * (w + gap)),
    y: round(top),
    w: round(w),
    h: round(h),
  }))
}

/** k tiles stacked in a column, top-aligned the way a sidebar reads. */
function placeColumn(k: number, area: Rect, gap: number): Rect[] {
  if (k === 0) return []
  const maxH = (area.h - gap * (k - 1)) / k
  const { w, h } = fitIn(area.w, maxH, ASPECT)
  const left = area.x + (area.w - w) / 2
  return Array.from({ length: k }, (_, i) => ({
    x: round(left),
    y: round(area.y + i * (h + gap)),
    w: round(w),
    h: round(h),
  }))
}

const roundRect = (r: Rect): Rect => ({
  x: round(r.x),
  y: round(r.y),
  w: round(r.w),
  h: round(r.h),
})

/** A tile in its area: the whole area with `fill`, else the largest centred `aspect` rectangle. */
/**
 * How tiles fill, when they do. On a computer a tile stays between 4:3 and
 * 16:9 — wider is a letterbox of forehead and chin, taller crops a
 * landscape camera to a sliver — and is centred in whatever is left over.
 * On a phone held upright a tile may be as tall as the screen, as Meet's
 * are.
 */
type FillMode = false | 'screen' | 'phone'
const WIDEST = 16 / 9
const TALLEST = 4 / 3
function fillArea(area: Rect, fill: 'screen' | 'phone'): Rect {
  if (area.h <= 0) return roundRect(area)
  const aspect = area.w / area.h
  if (aspect > WIDEST) return fitCentred(area, WIDEST)
  if (fill === 'screen' && aspect < TALLEST) return fitCentred(area, TALLEST)
  return roundRect(area)
}

/** A tile in its area: filled (up to 16:9) with `fill`, else the largest centred `aspect` rectangle. */
const place = (area: Rect, aspect: number, fill: FillMode): Rect =>
  fill ? fillArea(area, fill) : fitCentred(area, aspect)

/**
 * With `fill`: the featured tile over a row of cards that keeps its size
 * and runs off the right edge when there are more than fit.
 */
function featuredWithStrip(
  stage: Rect,
  gap: number,
  k: number,
  fill: 'screen' | 'phone',
  insets: Insets,
  presenting = false,
): { main: Rect; cards: Rect[]; strip: NonNullable<LayoutResult['strip']> } {
  if (fill === 'phone') {
    // The featured person runs under the bars, edge to edge; the cards float
    // over them, clear of the controls. A presentation keeps clear of both:
    // its words matter more than its edges.
    const { w, h } = PHONE_CARD
    const y = stage.h - insets.bottom - h - gap
    const top = presenting ? insets.top : 0
    return {
      main: presenting
        ? roundRect({ x: 0, y: top, w: stage.w, h: Math.max(0, y - gap - top) })
        : roundRect(stage),
      cards: Array.from({ length: k }, (_, i) => ({ x: i * (w + gap), y, w, h })),
      strip: { y, h, width: k * w + Math.max(0, k - 1) * gap },
    }
  }
  const h = round(clamp(stage.h * 0.18, 72, 136))
  const w = round(h * CARD_ASPECT)
  const y = stage.h - h
  return {
    main: fillArea({ x: 0, y: 0, w: stage.w, h: Math.max(0, y - gap) }, fill),
    cards: Array.from({ length: k }, (_, i) => ({ x: i * (w + gap), y, w, h })),
    strip: { y, h, width: k * w + Math.max(0, k - 1) * gap },
  }
}

type Insets = { top: number; bottom: number }

/**
 * A phone's grid: the screen shared out, no letterboxing. One or two
 * columns held upright (three for six on its side), every row as tall as
 * the next, and a last row with fewer people stretched across, so no
 * corner is left empty.
 */
function phoneGrid(k: number, area: Rect, gap: number): Rect[] {
  if (k === 0) return []
  const portrait = area.h >= area.w
  const cols = portrait ? (k <= 3 ? 1 : 2) : k <= 3 ? k : k === 4 ? 2 : 3
  const rows = Math.ceil(k / cols)
  const rowH = (area.h - gap * (rows - 1)) / rows
  const rects: Rect[] = []
  for (let row = 0; row < rows; row++) {
    const inRow = Math.min(cols, k - row * cols)
    const cellW = (area.w - gap * (inRow - 1)) / inRow
    for (let c = 0; c < inRow; c++) {
      rects.push(
        roundRect({
          x: area.x + c * (cellW + gap),
          y: area.y + row * (rowH + gap),
          w: cellW,
          h: rowH,
        }),
      )
    }
  }
  return rects
}

/** Whether this layout is for a big meeting, and the screen's pixel ratio: set once per computeLayout, read by cameraTile. */
let largeMeeting = false
let pixelRatio = 1

function cameraTile(p: ParticipantView, r: Rect, primary: boolean): LayoutTile {
  return {
    peerId: p.peerId,
    kind: 'camera',
    ...r,
    z: 1,
    quality: p.videoOn ? qualityForTile(r.w, r.h, pixelRatio, largeMeeting) : 'off',
    showVideo: p.videoOn,
    primary,
  }
}

export function computeLayout(input: LayoutInput): LayoutResult {
  largeMeeting = input.large === true
  pixelRatio = input.pixelRatio ?? 1
  const gap = input.gap ?? DEFAULT_GAP
  const maxTiles = Math.max(1, input.maxTiles ?? DEFAULT_MAX_TILES)
  const fill: FillMode = input.fill ? (input.isMobile ? 'phone' : 'screen') : false
  const W = Math.max(0, input.containerW)
  const H = Math.max(0, input.containerH)
  const stage: Rect = { x: 0, y: 0, w: W, h: H }
  const insets: Insets = input.insets ?? { top: 0, bottom: 0 }

  // Others in their seats' order (arrival order without one), ourselves
  // last — the way Meet does it, and the way people expect: the room
  // first, then "you".
  const seat = new Map((input.order ?? []).map((id, i) => [id, i]))
  const rank = (p: ParticipantView): number => seat.get(p.peerId) ?? Number.MAX_SAFE_INTEGER
  const byArrival = [...input.participants].sort(
    (a, b) => rank(a) - rank(b) || a.joinedAt - b.joinedAt,
  )
  const everyone = [...byArrival.filter((p) => !p.isLocal), ...byArrival.filter((p) => p.isLocal)]
  const byId = new Map(everyone.map((p) => [p.peerId, p]))
  const present = (id: PeerId | null | undefined): id is PeerId =>
    Boolean(id) && byId.has(id as PeerId)

  if (everyone.length === 0) return { mode: 'grid', tiles: [], hidden: [], primaryBy: 'none' }

  // Precedence: screen share > host focus > local pin > auto spotlight > auto.
  const share = input.screenShare && present(input.screenShare.peerId) ? input.screenShare : null
  let primaryBy: LayoutResult['primaryBy'] = 'none'
  let primaryId: PeerId | null = null
  if (present(input.focusedPeerId)) {
    primaryId = input.focusedPeerId
    primaryBy = 'host'
  } else if (present(input.localPinnedPeerId)) {
    primaryId = input.localPinnedPeerId
    primaryBy = 'pin'
  } else if (present(input.autoSpotlightPeerId) && !byId.get(input.autoSpotlightPeerId)?.isLocal) {
    // Never ourselves: Meet does not put your own face up because you
    // talked, and neither do we — everyone else sees you centre stage.
    primaryId = input.autoSpotlightPeerId
    primaryBy = 'auto'
  }

  // Ourselves apart, as a picture over the others — unless we are what the
  // stage features, or there is nobody else.
  const local = everyone.find((p) => p.isLocal)
  const floating =
    input.selfView === 'float' && local && everyone.length > 1 && primaryId !== local.peerId
      ? local.peerId
      : undefined
  const ordered = floating ? everyone.filter((p) => p.peerId !== floating) : everyone
  const done = (r: LayoutResult): LayoutResult => (floating ? { ...r, floating } : r)

  if (share) {
    return done(
      presentation(share, ordered, byId, stage, gap, maxTiles, input.isMobile, fill, insets),
    )
  }

  const anyVideo = ordered.some((p) => p.videoOn)
  const portrait = H > W

  let mode: ResolvedMode
  switch (input.mode) {
    case 'spotlight':
      mode = 'spotlight'
      break
    case 'sidebar':
      // A sidebar needs width. On a phone, or any portrait stage, the strip
      // goes below instead — which is spotlight. The window's stage (`fill`)
      // always puts the people in the strip below, as the designs do.
      mode = input.isMobile || portrait || fill ? 'spotlight' : 'sidebar'
      break
    case 'grid':
      mode = anyVideo ? 'grid' : 'avatar-grid'
      break
    default: {
      // Auto, modelled on Meet. A host focus or a pin always makes a
      // spotlight. The auto spotlight does too — two people included: the
      // one who has been talking takes centre stage — but only while
      // somebody has a camera on: one avatar featured over other avatars
      // says nothing.
      const autoLed = primaryBy === 'auto'
      if (primaryId && (!autoLed || anyVideo)) mode = 'spotlight'
      else if (ordered.length === 2 && anyVideo) mode = 'equal-split'
      else if (!anyVideo) mode = 'avatar-grid'
      else mode = 'grid'
    }
  }

  // Spotlight and sidebar need a main tile even when nobody chose one: the
  // first person who is not us.
  if ((mode === 'spotlight' || mode === 'sidebar') && !primaryId) {
    primaryId = ordered[0]?.peerId ?? null
  }

  const page = input.page ?? 0
  switch (mode) {
    case 'spotlight':
      return done(
        spotlight(
          primaryId as PeerId,
          primaryBy,
          ordered,
          byId,
          stage,
          gap,
          maxTiles,
          fill,
          insets,
        ),
      )
    case 'sidebar':
      return done(
        sidebar(primaryId as PeerId, primaryBy, ordered, byId, stage, gap, maxTiles, fill),
      )
    case 'equal-split':
      return done(equalSplit(ordered, stage, gap, portrait, fill))
    case 'avatar-grid':
      // Filling, a camera-off grid is the grid: the cells are the tiles.
      return done(
        fill
          ? { ...grid(ordered, stage, gap, maxTiles, fill, page, insets), mode: 'avatar-grid' }
          : avatarGrid(ordered, stage, gap, maxTiles),
      )
    default:
      return done(grid(ordered, stage, gap, maxTiles, fill, page, insets))
  }
}

function grid(
  ordered: ParticipantView[],
  stage: Rect,
  gap: number,
  maxTiles: number,
  fill: FillMode,
  page = 0,
  insets: Insets = { top: 0, bottom: 0 },
): LayoutResult {
  // A meeting bigger than a page is paged, not shrunk to postage stamps.
  const pages = Math.max(1, Math.ceil(ordered.length / maxTiles))
  const on = clamp(Math.floor(page), 0, pages - 1)
  const visible = ordered.slice(on * maxTiles, (on + 1) * maxTiles)
  const shown = new Set(visible.map((p) => p.peerId))
  const hidden = ordered.filter((p) => !shown.has(p.peerId)).map((p) => p.peerId)
  if (fill === 'phone') {
    // One person runs under the bars, edge to edge; several keep clear of
    // them, so no name or face hides under the controls.
    const area =
      visible.length === 1
        ? stage
        : {
            x: gap,
            y: insets.top,
            w: Math.max(0, stage.w - gap * 2),
            h: Math.max(0, stage.h - insets.top - insets.bottom),
          }
    const rects = phoneGrid(visible.length, area, gap)
    return {
      mode: 'grid',
      tiles: visible.map((p, i) => cameraTile(p, rects[i] as Rect, false)),
      hidden,
      primaryBy: 'none',
      page: on,
      pages,
    }
  }
  if (fill === 'screen' && followsShapes(visible)) {
    const rects = justifiedRows(visible.map(shapeOf), stage, gap)
    return {
      mode: 'grid',
      tiles: visible.map((p, i) => cameraTile(p, rects[i] as Rect, false)),
      hidden,
      primaryBy: 'none',
      page: on,
      pages,
    }
  }
  const g = bestGrid(visible.length, stage.w, stage.h, gap, ASPECT)
  // Filling, the tiles take their whole cell — the grid 16:9 tiles would
  // have, stretched to the stage — but a cell wider than 16:9 stays 16:9,
  // and the rows are centred.
  const cellW = (stage.w - gap * (g.cols - 1)) / g.cols
  const cellH = (stage.h - gap * (g.rows - 1)) / g.rows
  const tileW = fill ? Math.min(cellW, cellH * WIDEST) : g.tileW
  const tileH = fill === 'screen' ? Math.min(cellH, tileW / TALLEST) : fill ? cellH : g.tileH
  const rects = placeGrid(visible.length, g.cols, g.rows, tileW, tileH, stage, gap)
  return {
    mode: 'grid',
    tiles: visible.map((p, i) => cameraTile(p, rects[i] as Rect, false)),
    hidden,
    primaryBy: 'none',
    page: on,
    pages,
  }
}

/** Compact, uniform, no aspect reserved for video nobody is sending. */
function avatarGrid(
  ordered: ParticipantView[],
  stage: Rect,
  gap: number,
  maxTiles: number,
): LayoutResult {
  const visible = ordered.slice(0, maxTiles)
  const hidden = ordered.slice(maxTiles).map((p) => p.peerId)
  const g = bestGrid(visible.length, stage.w, stage.h, gap, 1)
  const cell = Math.min(g.tileW, AVATAR_CELL_MAX)
  const rects = placeGrid(visible.length, g.cols, g.rows, cell, cell, stage, gap)
  return {
    mode: 'avatar-grid',
    tiles: visible.map((p, i) => cameraTile(p, rects[i] as Rect, false)),
    hidden,
    primaryBy: 'none',
  }
}

/** Two people: 50/50, side by side on a landscape stage, stacked on a portrait one. */
function equalSplit(
  ordered: ParticipantView[],
  stage: Rect,
  gap: number,
  portrait: boolean,
  fill: FillMode,
): LayoutResult {
  if (fill === 'screen' && followsShapes(ordered)) {
    const rects = justifiedRows(ordered.map(shapeOf), stage, gap)
    return {
      mode: 'equal-split',
      tiles: ordered.map((p, i) => cameraTile(p, rects[i] as Rect, false)),
      hidden: [],
      primaryBy: 'none',
    }
  }
  const cells: Rect[] = portrait
    ? [
        { x: 0, y: 0, w: stage.w, h: (stage.h - gap) / 2 },
        { x: 0, y: (stage.h + gap) / 2, w: stage.w, h: (stage.h - gap) / 2 },
      ]
    : [
        { x: 0, y: 0, w: (stage.w - gap) / 2, h: stage.h },
        { x: (stage.w + gap) / 2, y: 0, w: (stage.w - gap) / 2, h: stage.h },
      ]
  return {
    mode: 'equal-split',
    tiles: ordered.map((p, i) => cameraTile(p, place(cells[i] as Rect, ASPECT, fill), false)),
    hidden: [],
    primaryBy: 'none',
  }
}

function spotlight(
  primaryId: PeerId,
  primaryBy: LayoutResult['primaryBy'],
  ordered: ParticipantView[],
  byId: Map<PeerId, ParticipantView>,
  stage: Rect,
  gap: number,
  maxTiles: number,
  fill: FillMode,
  insets: Insets,
): LayoutResult {
  const primary = byId.get(primaryId) as ParticipantView
  const others = ordered.filter((p) => p.peerId !== primaryId)
  if (others.length === 0) {
    return {
      mode: 'spotlight',
      tiles: [cameraTile(primary, place(stage, ASPECT, fill), true)],
      hidden: [],
      primaryBy,
    }
  }
  if (fill) {
    // The strip scrolls, so it holds more than a page — but not a thousand.
    const room = Math.max(maxTiles - 1, STRIP_MAX)
    const visible = others.slice(0, room)
    const { main, cards, strip } = featuredWithStrip(stage, gap, visible.length, fill, insets)
    return {
      mode: 'spotlight',
      tiles: [
        cameraTile(primary, main, true),
        ...visible.map((p, i) => cameraTile(p, cards[i] as Rect, false)),
      ],
      hidden: others.slice(room).map((p) => p.peerId),
      primaryBy,
      strip,
    }
  }
  const stripH = clamp(stage.h * 0.2, 64, 180)
  const main = fitCentred({ x: 0, y: 0, w: stage.w, h: stage.h - stripH - gap }, ASPECT)
  const visible = others.slice(0, maxTiles - 1)
  const hidden = others.slice(maxTiles - 1).map((p) => p.peerId)
  const strip = placeStrip(
    visible.length,
    { x: 0, y: stage.h - stripH, w: stage.w, h: stripH },
    gap,
  )
  return {
    mode: 'spotlight',
    tiles: [
      cameraTile(primary, main, true),
      ...visible.map((p, i) => cameraTile(p, strip[i] as Rect, false)),
    ],
    hidden,
    primaryBy,
  }
}

function sidebar(
  primaryId: PeerId,
  primaryBy: LayoutResult['primaryBy'],
  ordered: ParticipantView[],
  byId: Map<PeerId, ParticipantView>,
  stage: Rect,
  gap: number,
  maxTiles: number,
  fill: FillMode,
): LayoutResult {
  const primary = byId.get(primaryId) as ParticipantView
  const others = ordered.filter((p) => p.peerId !== primaryId)
  if (others.length === 0) {
    return {
      mode: 'sidebar',
      tiles: [cameraTile(primary, place(stage, ASPECT, fill), true)],
      hidden: [],
      primaryBy,
    }
  }
  const sideW = clamp(stage.w * 0.24, 160, 320)
  const main = place({ x: 0, y: 0, w: stage.w - sideW - gap, h: stage.h }, ASPECT, fill)
  const visible = others.slice(0, maxTiles - 1)
  const hidden = others.slice(maxTiles - 1).map((p) => p.peerId)
  const column = placeColumn(
    visible.length,
    { x: stage.w - sideW, y: 0, w: sideW, h: stage.h },
    gap,
  )
  return {
    mode: 'sidebar',
    tiles: [
      cameraTile(primary, main, true),
      ...visible.map((p, i) => cameraTile(p, column[i] as Rect, false)),
    ],
    hidden,
    primaryBy,
  }
}

/**
 * A screen share takes ~78% of the stage; the people go in a filmstrip, the
 * presenter's own camera first so it sits next to the share.
 */
function presentation(
  share: ScreenShareView,
  ordered: ParticipantView[],
  byId: Map<PeerId, ParticipantView>,
  stage: Rect,
  gap: number,
  maxTiles: number,
  isMobile: boolean,
  fill: FillMode,
  insets: Insets,
): LayoutResult {
  // The presenter's camera sits next to the share — unless it floats apart.
  const presenter = byId.get(share.peerId) as ParticipantView
  const people = ordered.some((p) => p.peerId === share.peerId)
    ? [presenter, ...ordered.filter((p) => p.peerId !== share.peerId)]
    : ordered
  const room = fill ? Math.max(maxTiles - 1, STRIP_MAX) : maxTiles - 1
  const visible = people.slice(0, room)
  const hidden = people.slice(room).map((p) => p.peerId)

  // Filling, the presentation is the featured tile and the people are the
  // strip under it, as with a spotlight.
  if (fill) {
    const { main, cards, strip } = featuredWithStrip(stage, gap, visible.length, fill, insets, true)
    return {
      mode: 'presentation',
      tiles: [
        {
          peerId: share.peerId,
          kind: 'screen',
          ...main,
          z: 1,
          quality: qualityForWidth(main.w),
          showVideo: true,
          primary: true,
        },
        ...visible.map((p, i) => cameraTile(p, cards[i] as Rect, false)),
      ],
      hidden,
      primaryBy: 'share',
      strip,
    }
  }

  const landscape = stage.w >= stage.h && !isMobile
  let shareRect: Rect
  let peopleRects: Rect[]
  if (landscape) {
    const shareW = stage.w * 0.78 - gap / 2
    shareRect = fitCentred({ x: 0, y: 0, w: shareW, h: stage.h }, ASPECT)
    peopleRects = placeColumn(
      visible.length,
      { x: shareW + gap, y: 0, w: stage.w - shareW - gap, h: stage.h },
      gap,
    )
  } else {
    const shareH = stage.h * 0.7 - gap / 2
    shareRect = fitCentred({ x: 0, y: 0, w: stage.w, h: shareH }, ASPECT)
    peopleRects = placeStrip(
      visible.length,
      { x: 0, y: shareH + gap, w: stage.w, h: stage.h - shareH - gap },
      gap,
    )
  }

  const screen: LayoutTile = {
    peerId: share.peerId,
    kind: 'screen',
    ...shareRect,
    z: 1,
    quality: qualityForWidth(shareRect.w),
    showVideo: true,
    primary: true,
  }
  return {
    mode: 'presentation',
    tiles: [screen, ...visible.map((p, i) => cameraTile(p, peopleRects[i] as Rect, false))],
    hidden,
    primaryBy: 'share',
  }
}
